import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * <Lightbox /> — full-screen zoom viewer with keyboard/tap dismiss.
 * Cinematic zoom-in transition (spring-based scale).
 */
export default function Lightbox({ image, onClose }) {
  useEffect(() => {
    if (!image) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [image, onClose]);

  return (
    <AnimatePresence>
      {image && (
        <motion.div
          className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4 sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          data-testid="lightbox-overlay"
        >
          <button
            onClick={onClose}
            aria-label="Close lightbox"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md grid place-items-center text-white transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 120, damping: 18 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-[92vw] max-h-[86vh]"
          >
            <img
              src={image.src}
              alt={image.alt || ""}
              className="max-w-full max-h-[86vh] object-contain rounded-2xl shadow-2xl"
            />
            {(image.title || image.subtitle) && (
              <div className="mt-4 text-center text-white">
                {image.subtitle && (
                  <div className="text-[11px] uppercase tracking-[0.28em] text-[var(--pam-gold)]">
                    {image.subtitle}
                  </div>
                )}
                {image.title && (
                  <div className="mt-1 font-serif text-2xl">{image.title}</div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
